import { ActiveScreen, ConnectionScreenState } from "@enums/screens.enum";
import { create } from "zustand";

import type { MaintenanceStatus, MaintenanceWindow } from "@app-types/maintenance.type";

export type MaintenanceSource = "flag" | "response";

export interface ISystemStore {
  isAppLoading: boolean;
  isAppActive: boolean;
  isAppOffline: boolean;
  isDarkMode: boolean;
  isMaintenance: boolean;
  maintenanceWindow: MaintenanceWindow | null;
  maintenanceMessage: string;
  maintenanceSource: MaintenanceSource | null;
  connectionAttempt: number;
  activeScreen: ActiveScreen;
  connectionScreenState: ConnectionScreenState;
  setIsAppLoading: (value: boolean) => void;
  setIsAppActive: (value: boolean) => void;
  setIsAppOffline: (value: boolean) => void;
  setIsDarkMode: (value: boolean) => void;
  applyMaintenanceStatus: (status: MaintenanceStatus) => void;
  setMaintenanceDetected: (window: MaintenanceWindow | null) => void;
  clearMaintenance: () => void;
  incrementConnectionAttempt: () => void;
  changeActiveScreen: (value: ActiveScreen) => void;
  setConnectionScreenState: (value: ConnectionScreenState) => void;
}

const useSystemStore = create<ISystemStore>((set) => ({
  isAppLoading: true,
  isAppActive: true,
  isAppOffline: false,
  isDarkMode: false,
  isMaintenance: false,
  maintenanceWindow: null,
  maintenanceMessage: "",
  maintenanceSource: null,
  connectionAttempt: 0,
  activeScreen: ActiveScreen.LOADING,
  connectionScreenState: ConnectionScreenState.Connect,
  setIsAppLoading: (value: boolean) => set({ isAppLoading: value }),
  setIsAppActive: (value: boolean) => set({ isAppActive: value }),
  setIsAppOffline: (value: boolean) => set({ isAppOffline: value }),
  setIsDarkMode: (value: boolean) => set({ isDarkMode: value }),

  /** The tools flag owns the planned path, but never clears a maintenance a `503` raised. */
  applyMaintenanceStatus: (status: MaintenanceStatus) =>
    set((state) => {
      if (status.isActive) {
        return {
          isMaintenance: true,
          maintenanceWindow: status.window ?? state.maintenanceWindow,
          maintenanceMessage: status.message,
          maintenanceSource: "flag" as const,
        };
      }

      if (state.maintenanceSource === "response") return state;

      return {
        isMaintenance: false,
        maintenanceWindow: status.window,
        maintenanceMessage: "",
        maintenanceSource: null,
      };
    }),

  /** Raised from the `503` interceptor. The flag is the stronger signal and wins. */
  setMaintenanceDetected: (window: MaintenanceWindow | null) =>
    set((state) => {
      if (state.isMaintenance && state.maintenanceSource === "flag") return state;

      return {
        isMaintenance: true,
        maintenanceWindow: window ?? state.maintenanceWindow,
        maintenanceSource: "response" as const,
      };
    }),

  /**
   * A backend that answers again only clears a `503`-raised maintenance, never the flag.
   * Drops the window/message too, so a window parsed from an old 503 body can't resurface
   * as the current one when the flag later activates without a window of its own.
   */
  clearMaintenance: () =>
    set((state) => {
      if (!state.isMaintenance || state.maintenanceSource === "flag") return state;

      return {
        isMaintenance: false,
        maintenanceWindow: null,
        maintenanceMessage: "",
        maintenanceSource: null,
      };
    }),
  // useConnection watches the count and re-runs the connection bootstrap on each bump.
  incrementConnectionAttempt: () =>
    set((state) => ({ connectionAttempt: state.connectionAttempt + 1 })),
  changeActiveScreen: (value: ActiveScreen) => set({ activeScreen: value }),
  setConnectionScreenState: (value: ConnectionScreenState) => set({ connectionScreenState: value }),
}));

export default useSystemStore;
